'use client';

import { SupplierRequestInput } from '@scaleup/models';
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useState,
} from 'react';
import { useSaveSupplierRequestDraftMutation } from '../../../api';
import { SupplierRequestStep } from './types';

type SupplierRequestWizardContextProps = {
  supplierRequestInput: SupplierRequestInput;
  setSupplierRequestInput: Dispatch<SetStateAction<SupplierRequestInput>>;
  handleChangeValue: (
    key: keyof SupplierRequestInput,
    value: SupplierRequestInput[keyof SupplierRequestInput]
  ) => void;
  currentStep: SupplierRequestStep;
  setCurrentStep: Dispatch<SetStateAction<SupplierRequestStep>>;
  flow: 'upload' | 'wizard';
  setFlow: Dispatch<SetStateAction<'upload' | 'wizard'>>;
  saveDraftLoading: boolean;
  saveSupplierRequestDraft: () => Promise<void>;
};

const SupplierRequestWizardContext =
  createContext<SupplierRequestWizardContextProps>(
    {} as SupplierRequestWizardContextProps
  );

export const useSupplierRequestWizardContext = () => {
  const context = useContext(SupplierRequestWizardContext);
  if (!context) {
    throw new Error('SupplierRequestWizardContext is missing');
  }

  return context;
};

export const SupplierRequestWizardContextProvider = ({
  children,
}: PropsWithChildren) => {
  const [currentStep, setCurrentStep] =
    useState<SupplierRequestStep>('INITIAL');
  const [flow, setFlow] = useState<'upload' | 'wizard'>('wizard');
  const [_saveSupplierRequestDraft, { isLoading: saveDraftLoading }] =
    useSaveSupplierRequestDraftMutation();

  const [supplierRequestInput, setSupplierRequestInput] =
    useState<SupplierRequestInput>({
      projectName: '',
      description: '',
      timeframe: '',
      budget: '',
      unitVolume: '',
      locations: '',
      questions: [],
      documents: [],
      supplierPreference: [],
      creatorName: '',
      creatorTitle: '',
      subCategory: '',
      category: '',
    });

  const handleChangeValue = (
    key: keyof SupplierRequestInput,
    value: SupplierRequestInput[keyof SupplierRequestInput]
  ) => {
    setSupplierRequestInput((oldSupplierRequestInput) => ({
      ...oldSupplierRequestInput,
      [key]: value,
    }));
  };

  const saveSupplierRequestDraft = async () => {
    await _saveSupplierRequestDraft(supplierRequestInput);
  };

  return (
    <SupplierRequestWizardContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        flow,
        setFlow,
        supplierRequestInput,
        setSupplierRequestInput,
        handleChangeValue,
        saveDraftLoading,
        saveSupplierRequestDraft,
      }}
    >
      {children}
    </SupplierRequestWizardContext.Provider>
  );
};
